import { useRef, useState } from "react";

import type { Direx, IncidentKind, NewIncident, Phase } from "../../api/types";
import { designate } from "../../components/attention";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Select } from "../../components/Select";
import { Button, Field, inputClass, Segmented, textareaClass } from "../../components/ui";
import { unitDesignation } from "../../i18n/designation";
import { k, t } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { useUi } from "../../state/ui";
import { useUnsavedDraft } from "../../state/unsavedDrafts";
import { UNASSIGNED } from "./common";

/** « Nouvel incident » : incident rédigé par l'animateur, numéroté hors des plages du catalogue (PER1…) et posé
 *  sur son unité cible au temps choisi. */

const KINDS: { value: IncidentKind; label: string }[] = [
  { value: "TAC", label: k("Tactique") },
  { value: "TEC", label: k("Technique") },
  { value: "ENV", label: k("Environnement") },
];
const TEMPOS: { value: Phase; label: string }[] = [
  { value: "T1", label: "T1" },
  { value: "T2", label: "T2" },
  { value: "T3", label: "T3" },
];
const DEFAULT_CELLULE: Record<IncidentKind, string> = { TAC: "S2", TEC: "S4", ENV: "S3" };

export function NewIncidentDialog({
  data,
  tempo,
  language,
  busy,
  onCreate,
  onClose,
}: {
  data: Direx;
  tempo: Phase;
  /** Langue du dossier : celle du format par défaut de l'incident (registre A69). */
  language: "fr" | "en";
  busy: boolean;
  onCreate: (incident: NewIncident) => void;
  onClose: () => void;
}) {
  const defaultFormat = language === "en" ? "EXCON message" : "Message EXCON";
  const notify = useUi((s) => s.notify);
  const titleRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<IncidentKind>("TAC");
  const [draft, setDraft] = useState({
    tempo,
    cellule: data.cellules.includes(DEFAULT_CELLULE.TAC) ? DEFAULT_CELLULE.TAC : (data.cellules[0] ?? "S3"),
    short: "",
    detail: "",
    objective: "",
    reaction: "",
    format: defaultFormat,
    target: data.targets[0]?.key ?? UNASSIGNED,
  });
  const changeKind = (next: IncidentKind) => {
    setKind(next);
    const cellule = DEFAULT_CELLULE[next];
    if (data.level === "gtia" && data.cellules.includes(cellule)) setDraft((d) => ({ ...d, cellule }));
  };
  // Texte saisi : la fenêtre ne se ferme plus d'un clic à côté ou d'Échap sans le demander, et le registre des
  // saisies retient la fermeture de l'onglet (audit de persistance M4).
  const dirty =
    [draft.short, draft.detail, draft.objective, draft.reaction].some((value) => value.trim() !== "") || draft.format !== defaultFormat;
  useUnsavedDraft("direx:new-incident", dirty, {
    label: t("Nouvel incident « {title} »", { title: draft.short.trim() || t("sans titre") }),
    discard: onClose,
    reveal: () => designate(titleRef.current, "field"),
  });
  const submit = () =>
    onCreate({
      type: kind,
      tempo: draft.tempo,
      cellule: draft.cellule,
      short: draft.short.trim(),
      detail: draft.detail.trim(),
      objective: draft.objective.trim(),
      reaction: draft.reaction.trim(),
      format: draft.format.trim() || defaultFormat,
      unit_key: draft.target === UNASSIGNED ? null : draft.target,
    });
  return (
    <Dialog
      title={t("Nouvel incident")}
      subtitle={t("Rédigé par l'animateur, ajouté à la DIREX de ce dossier")}
      onClose={onClose}
      dirty={dirty}
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("Annuler")}</Button>
          <Button
            onClick={submit}
            disabled={busy}
            blocked={!draft.short.trim()}
            onBlocked={() => {
              // Sans titre, l'incident ne peut être créé : le champ titre est l'action attendue (R12).
              designate(titleRef.current, "field");
              notify({ text: t("Donnez un titre à l'incident."), tone: "info" });
            }}
          >
            {t("Créer l'incident")}
          </Button>
        </>
      }
    >
      <DialogBody>
        <Segmented label={t("Type")} options={KINDS.map((o) => ({ ...o, label: t(o.label) }))} value={kind} onChange={changeKind} />
        <div className="grid grid-cols-2 gap-3">
          <Segmented label={t("Temps")} options={TEMPOS} value={draft.tempo} onChange={(t) => setDraft({ ...draft, tempo: t })} />
          <Field label={t("Cellule")}>
            <Select className={inputClass} value={draft.cellule} onChange={(e) => setDraft({ ...draft, cellule: e.target.value })}>
              {data.cellules.map((c) => (
                <option key={c} value={c}>{term(c)}</option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label={t("Titre")}>
          <input ref={titleRef} autoFocus className={inputClass} maxLength={200} value={draft.short} placeholder={t("Ex. : Panne du groupe électrogène du PC")}
            onChange={(e) => setDraft({ ...draft, short: e.target.value })} />
        </Field>
        {(
          [
            ["detail", t("Description"), 3],
            ["objective", t("Objectif d'entraînement"), 2],
            ["reaction", t("Réaction attendue"), 2],
          ] as const
        ).map(([key, label, rows]) => (
          <Field key={key} label={label}>
            <textarea rows={rows} value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              className={textareaClass} />
          </Field>
        ))}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("Cible")}>
            <Select className={inputClass} value={draft.target} onChange={(e) => setDraft({ ...draft, target: e.target.value })}>
              {data.targets.map((target) => (
                <option key={target.key} value={target.key}>
                  {target.commander ? t("{label} (CO)", { label: unitDesignation(target.label) }) : unitDesignation(target.label)}
                </option>
              ))}
              <option value={UNASSIGNED}>{t("Éventuel (sans cible)")}</option>
            </Select>
          </Field>
          <Field label={t("Format d'inject")}>
            <input className={inputClass} maxLength={80} value={draft.format} onChange={(e) => setDraft({ ...draft, format: e.target.value })} />
          </Field>
        </div>
      </DialogBody>
    </Dialog>
  );
}
